// ─── Cross-document View Transition detection ──────────────────────────────
// The `pagereveal` event fires before first render on browsers that support
// cross-document View Transitions (Chrome 126+, Edge 126+, Safari 18.2+).
// When a view transition is active, the browser already cross-fades the full
// page snapshot, so VScrollReveal should skip its first-screen entrance
// animation to avoid a double fade-in.
// This flag is set synchronously before any class constructors run because
// `pagereveal` fires before DOMContentLoaded and before module-level code
// that follows this listener.
let _viewTransitionActive = false;
if ('onpagereveal' in window) {
  window.addEventListener('pagereveal', (e) => {
    _viewTransitionActive = !!e.viewTransition;
  }, { once: true });
}

/**
 * VScrollReveal
 *
 * Global fade-in-from-bottom entrance animation for all sections.
 *
 * Two strategies depending on initial viewport position:
 *   - First-screen (above the fold): CSS @keyframes animation
 *     (.v-reveal-entrance) plays immediately on page load.
 *   - Below-fold: IntersectionObserver + CSS transition (.v-reveal)
 *     triggers when the element scrolls into view.
 *
 * Behaviour:
 * - Only targets sections inside #main. The header-group and overlay-group
 *   (cart-drawer, search-drawer, newsletter-popup, privacy-banner) are rendered
 *   outside #main in theme.liquid and are therefore automatically excluded.
 * - Grid items (product cards, multi-column items, etc.) stagger in sequence.
 * - Section headers animate as a single block before the grid items below.
 * - Sections without grid items animate their .section-stack as a whole.
 * - Auto-fallback: any section not matched by the rules above has its root
 *   wrapper animated as a whole block, so every section gets the entrance
 *   animation without manual data-v-reveal attributes.
 * - Sections with [data-v-animate] are skipped by the auto-fallback — they
 *   have a dedicated V[Name]Animate class with custom entrance animation.
 * - data-v-reveal is OPTIONAL — use it only when you need fine-grained
 *   stagger control within a section (heading → body → cta).
 */
class VScrollReveal {
  /**
   * Selectors whose matching elements animate with a stagger delay.
   * Each sibling group (same parent) staggers independently.
   */
  static STAGGER_SELECTORS = [
    '.multi-column__item',
    '.product-card',
    '.blog-post-card',
    '.logo-list__item',
    '.collection-list__item',
    '.image-link-blocks__item',
    '.hot-spots__product',
  ].join(',');

  constructor() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this._observed = new WeakSet();
    this._observer = new IntersectionObserver(this._onIntersect.bind(this), {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px',
    });

    // When arriving via a cross-document View Transition the browser already
    // animates the entire page snapshot (::view-transition-new(root)).
    // Skip the per-element first-screen entrance to avoid a double fade-in.
    this._skipFirstScreenEntrance = _viewTransitionActive;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._setup());
    } else {
      this._setup();
    }
  }

  _setup() {
    const mainEl = document.getElementById('main');
    if (!mainEl) return;

    const sections = mainEl.querySelectorAll('[id^="shopify-section-"]');

    sections.forEach((section) => {
      let revealed = false;

      // 1. Section header — always animate as a block
      const header = section.querySelector('.section-header');
      if (header) { this._addReveal(header); revealed = true; }

      // 2. Find all stagger-eligible grid items within this section
      const staggerItems = Array.from(
        section.querySelectorAll(VScrollReveal.STAGGER_SELECTORS)
      );

      if (staggerItems.length > 0) {
        const groups = new Map();
        staggerItems.forEach((item) => {
          const parent = item.parentElement;
          if (!groups.has(parent)) groups.set(parent, []);
          groups.get(parent).push(item);
        });

        groups.forEach((items) => {
          items.forEach((item, i) => {
            this._addReveal(item, i > 0 ? Math.min(i, 6) : 0);
          });
        });
        revealed = true;
      } else {
        // 3. No grid items — animate the section-stack content block as a whole.
        const stack = section.querySelector('.section-stack');
        const hasChartCards = section.querySelector('[data-v-chart]');
        if (stack && stack !== header && !hasChartCards) {
          this._addReveal(stack);
          revealed = true;
        }
      }

      // 4. Explicit reveal targets — optional fine-grained stagger control.
      //    First-screen elements automatically get @keyframes entrance;
      //    below-fold elements use IntersectionObserver as usual.
      section.querySelectorAll('[data-v-reveal]').forEach((el) => {
        const delay = parseInt(el.dataset.vRevealDelay, 10) || 0;
        this._addReveal(el, delay);
        revealed = true;
      });

      // 5. Auto-fallback — if nothing above matched, reveal the section's
      //    root wrapper as a whole block. This ensures every section gets
      //    the fade-in/slide-up animation without manual data-v-reveal.
      //    Skip sections marked with [data-v-animate] — those have a
      //    dedicated V[Name]Animate class with its own entrance animation.
      if (!revealed && !section.querySelector('[data-v-animate]')) {
        let root = section.firstElementChild;
        while (root && (root.tagName === 'STYLE' || root.tagName === 'SCRIPT' || root.tagName === 'LINK')) {
          root = root.nextElementSibling;
        }
        if (root) this._addReveal(root);
      }
    });
  }

  /**
   * Marks an element for scroll-reveal animation.
   *
   * - First-screen elements (already in viewport) receive a CSS @keyframes
   *   animation (.v-reveal-entrance) that plays immediately on page load.
   * - Below-fold elements receive the transition-based .v-reveal class and
   *   are observed by IntersectionObserver for scroll-triggered entrance.
   *
   * @param {Element} el    Target element
   * @param {number}  delay Stagger index (0 = no delay, 1-6 = mapped to CSS)
   */
  _addReveal(el, delay = 0) {
    if (this._observed.has(el)) return;
    this._observed.add(el);

    const rect = el.getBoundingClientRect();
    const inViewport = rect.top < window.innerHeight && rect.bottom > 0;

    if (inViewport) {
      if (this._skipFirstScreenEntrance) {
        // View Transition already provides the page-level fade-in;
        // show first-screen elements immediately to avoid double animation.
        return;
      }
      el.classList.add('v-reveal-entrance');
      if (delay > 0) el.dataset.delay = delay;
      el.addEventListener('animationend', () => {
        el.classList.remove('v-reveal-entrance');
        el.style.willChange = 'auto';
        if (delay > 0) delete el.dataset.delay;
      }, { once: true });
      return;
    }

    el.classList.add('v-reveal');
    if (delay > 0) el.dataset.delay = delay;
    this._observer.observe(el);
  }

  _onIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const el = entry.target;
        el.classList.add('is-visible');
        this._observer.unobserve(el);

        // After the reveal transition finishes, remove will-change to release
        // the compositing layer. A persistent will-change on an ancestor
        // isolates descendant backdrop-filter into its own layer, breaking blur.
        // Use the longest possible delay (data-delay="6" = 0.48s) + duration (0.65s)
        // + buffer to guarantee the transition has fully settled.
        const delay = parseFloat(el.dataset.delay || 0) * 0.08;
        setTimeout(() => { el.style.willChange = 'auto'; }, (0.65 + delay) * 1000 + 100);
      }
    });
  }
}

new VScrollReveal();

// ─── Shared countUp utilities ────────────────────────────────────────────────

/**
 * vCountUpRaw
 *
 * Counts a single DOM element's textContent from 0 to `target` over `duration` ms.
 * Uses a requestAnimationFrame loop with an ease-out cubic curve.
 *
 * @param {Element} el        - Element whose textContent to update
 * @param {number}  target    - Final integer value
 * @param {number}  duration  - Animation duration in ms
 * @param {boolean} zeroPad   - If true, always output 2-digit zero-padded string (e.g. "05")
 */
function vCountUpRaw(el, target, duration, zeroPad = false) {
  const startTime = performance.now();
  const tick = (now) => {
    const t     = Math.max(0, Math.min((now - startTime) / duration, 1));
    const eased = 1 - Math.pow(1 - t, 3);
    const value = Math.round(eased * target);
    el.textContent = zeroPad ? String(value).padStart(2, '0') : String(value);
    if (t < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/**
 * vCountUp
 *
 * Counts a number element from 0 to `target` over `duration` ms.
 *
 * DOM contract (same as v-count-up.liquid):
 *   - If `el` contains a [data-count-number] child, only that child's textContent
 *     is updated, leaving any sibling unit text untouched.
 *   - Outputs thousand-separator commas when target >= 1000.
 *   - Resets to "0" before starting.
 *
 * @param {Element} el        - Container element or the number element itself
 * @param {number}  target    - Final value
 * @param {number}  duration  - Animation duration in ms (default 1200)
 */
function vCountUp(el, target, duration = 1200) {
  const numEl     = el.querySelector('[data-count-number]') || el;
  const useCommas = target >= 1000;
  numEl.textContent = '0';
  const startTime = performance.now();
  const tick = (now) => {
    const t     = Math.max(0, Math.min((now - startTime) / duration, 1));
    const eased = 1 - Math.pow(1 - t, 3);
    const value = Math.round(eased * target);
    numEl.textContent = useCommas ? value.toLocaleString('en-US') : String(value);
    if (t < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/**
 * vAnimateCountTarget
 *
 * Runs countUp on all [data-count-target] elements inside `container`.
 * Supports three value formats (auto-detected from data-count-target):
 *
 *   Plain number  "62"    → [data-count-number] counts 0 → 62
 *   Time format   "9:30"  → [data-count-number] counts 0 → 9 (hour)
 *                           [data-count-colon]  shows ":"  (static)
 *                           [data-count-suffix-number] counts 0 → 30 (minute,
 *                             zero-padded when original had leading zero)
 *   Fraction      "68/100" → [data-count-number] counts 0 → 68 (numerator)
 *                           [data-count-colon]  shows "/"  (static)
 *                           [data-count-suffix-number] static denominator "100"
 *
 * DOM contract (produced by v-count-up.liquid):
 *   <div data-count-target="9:30">
 *     <span data-count-number>9:30</span>
 *     <span data-count-colon></span>
 *     <span data-count-suffix-number></span>
 *   </div>
 *
 * @param {Element} container      - Root element to search within
 * @param {boolean} reducedMotion  - When true, skips animation and shows final value
 * @param {number}  duration       - Animation duration in ms (default 1200)
 * @param {Object}  options        - Optional. skipRingProgress: skip .v-ring-progress__number
 *                                   (those are driven by vAnimateWidgetContainer ring step)
 */
function vAnimateCountTarget(container, reducedMotion = false, duration = 1200, options = {}) {
  const skipRingProgress = options.skipRingProgress === true;

  container.querySelectorAll('[data-count-target]').forEach((el) => {
    if (skipRingProgress && el.classList.contains('v-ring-progress__number')) return;

    // Freeze the width of the number container so the unit sibling
    // stays fixed in place while digits count up (width changes).
    const numEl = el.querySelector('[data-count-number]');
    const numContainer = numEl && numEl.parentElement !== el ? numEl.parentElement : null;
    if (numContainer && !numContainer.dataset.widthFrozen) {
      numContainer.style.width = numContainer.offsetWidth + 'px';
      numContainer.dataset.widthFrozen = '1';
    }

    const raw = String(el.dataset.countTarget).trim();

    // Detect time format: digits : digits  (e.g. "9:30", "10:05")
    const timeMatch = raw.match(/^(\d+):(\d+)$/);

    if (timeMatch) {
      const hourTarget  = parseInt(timeMatch[1], 10);
      const minTarget   = parseInt(timeMatch[2], 10);
      const minPadded   = timeMatch[2].length === 2 && timeMatch[2][0] === '0';

      const countNumEl    = el.querySelector('[data-count-number]');
      const colonEl       = el.querySelector('[data-count-colon]');
      const suffixNumEl   = el.querySelector('[data-count-suffix-number]');

      if (colonEl) colonEl.textContent = ':';

      if (reducedMotion) {
        if (countNumEl)  countNumEl.textContent  = String(hourTarget);
        if (suffixNumEl) suffixNumEl.textContent = minPadded
          ? String(minTarget).padStart(2, '0')
          : String(minTarget);
      } else {
        if (countNumEl) {
          countNumEl.textContent = '0';
          vCountUpRaw(countNumEl, hourTarget, duration, false);
        }
        if (suffixNumEl) {
          suffixNumEl.textContent = minPadded ? '00' : '0';
          vCountUpRaw(suffixNumEl, minTarget, duration, minPadded);
        }
      }
    } else {
      const fracMatch = raw.match(/^(\d+)\/(\d+)$/);
      if (fracMatch) {
        const numTarget = parseInt(fracMatch[1], 10);
        const denTarget = parseInt(fracMatch[2], 10);

        const countNumEl  = el.querySelector('[data-count-number]');
        const colonEl     = el.querySelector('[data-count-colon]');
        const suffixNumEl = el.querySelector('[data-count-suffix-number]');

        if (colonEl) colonEl.textContent = '/';
        if (suffixNumEl) suffixNumEl.textContent = String(denTarget);

        // Freeze the numerator span width to the final value's width so that
        // digit-count changes (1 → 2 → 3 chars) don't push '/' and the
        // denominator sideways during the count-up.
        if (countNumEl && !countNumEl.dataset.fracMinW) {
          const finalText = numTarget >= 1000
            ? numTarget.toLocaleString('en-US')
            : String(numTarget);
          const prev = countNumEl.textContent;
          countNumEl.textContent = finalText;
          const finalWidth = countNumEl.getBoundingClientRect().width;
          countNumEl.textContent = prev;
          countNumEl.style.display = 'inline-block';
          countNumEl.style.minWidth = finalWidth + 'px';
          countNumEl.style.textAlign = 'right';
          countNumEl.dataset.fracMinW = '1';
        }

        if (reducedMotion) {
          if (countNumEl) {
            countNumEl.textContent = numTarget >= 1000
              ? numTarget.toLocaleString('en-US')
              : String(numTarget);
          }
        } else if (!isNaN(numTarget)) {
          vCountUp(el, numTarget, duration);
        }
      } else {
        // Plain number format
        const target = parseFloat(raw.replace(/,/g, ''));
        if (!isNaN(target)) {
          const colonEl     = el.querySelector('[data-count-colon]');
          const suffixNumEl = el.querySelector('[data-count-suffix-number]');
          if (colonEl)     colonEl.textContent     = '';
          if (suffixNumEl) suffixNumEl.textContent = '';

          if (reducedMotion) {
            const countNumEl = el.querySelector('[data-count-number]') || el;
            countNumEl.textContent = target >= 1000
              ? target.toLocaleString('en-US')
              : String(Math.round(target));
          } else {
            vCountUp(el, target, duration);
          }
        }
      }
    }
  });
}

window.vAnimateCountTarget = vAnimateCountTarget;

// ─────────────────────────────────────────────────────────────────────────────

/**
 * vAnimateWidgetContainer
 *
 * Universal widget animation driver. Runs all supported animation types
 * found inside `container` in a single pass:
 *
 *   1. Ring progress arcs   — stroke-dashoffset fill + countUp on the label number
 *   2. CountUp numbers      — all [data-count-target] elements (ring numbers excluded,
 *                             they are driven by the ring arc handler above)
 *   3. Gradient sparklines  — clipPath-rect reveal for lines whose stroke is a
 *                             <linearGradient> (stroke-dashoffset can't animate those).
 *                             Detected via [data-sparkline-clip] on the SVG element:
 *                               data-sparkline-clip="<line-selector>|<area-selector>|<width>|<height>"
 *                             e.g. data-sparkline-clip=".v-widget-energy__line|.v-widget-energy__area|244|75"
 *   4. Solid-stroke lines   — stroke-dashoffset draw for plain-color SVG lines.
 *                             Detected via [data-sparkline-dash] on the <path>/<polyline> element.
 *                             The element must also have a paired [data-sparkline-area] sibling
 *                             (optional) for the fill fade-in.
 *
 * Called by:
 *   - VHeroTechnologyAnimate._animatePanel  (tab-activated panels)
 *   - VWidgetAnimate._animateWidget         (standalone widgets in any section)
 *
 * @param {Element} container      - Root element to search within
 * @param {boolean} reducedMotion  - When true, skips animation and shows final state
 */
function vAnimateWidgetContainer(container, reducedMotion = false, options = {}) {
  const skipRingTargets = options.skipRingTargets === true;
  const skipCountTargets = options.skipCountTargets === true;

  // ── 1. Ring progress (→ snippets/v-ring-progress.liquid {% javascript %}) ──
  if (typeof window.vAnimateRingProgressIn === 'function') {
    window.vAnimateRingProgressIn(container, reducedMotion, options);
  } else {
    container.querySelectorAll('.v-ring-progress__arc').forEach((arc) => {
      const ring = arc.closest('.v-ring-progress');
      if (!ring) return;

      const circumference = parseFloat(ring.dataset.ringCircumference);
      const value         = parseFloat(ring.dataset.ringValue);
      const targetOffset  = parseFloat(arc.dataset.targetOffset);
      const numEl         = ring.querySelector('.v-ring-progress__number');

      if (isNaN(circumference) || isNaN(targetOffset)) return;

      if (reducedMotion) {
        arc.style.strokeDashoffset = targetOffset;
        if (numEl) numEl.textContent = Math.round(value);
        return;
      }

      arc.classList.add('is-animating');
      arc.style.strokeDashoffset = targetOffset;
      if (numEl && !isNaN(value)) vCountUp(numEl, value, 1300);
    });

    if (!skipRingTargets) {
      container.querySelectorAll('[data-ring-target]').forEach((arc) => {
        const target = parseFloat(arc.dataset.ringTarget);
        if (isNaN(target)) return;

        const circ = parseFloat(arc.getAttribute('stroke-dasharray'));
        if (isNaN(circ)) return;

        arc.style.strokeDashoffset = String(circ);

        if (reducedMotion) {
          arc.style.strokeDashoffset = String(target);
          return;
        }

        arc.style.transition = 'none';
        requestAnimationFrame(() => {
          arc.style.transition = 'stroke-dashoffset 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          arc.style.strokeDashoffset = String(target);
        });
      });
    }

  }

  // ── 1a. Ring progress gap (v-ring-progress-gap snippet owns this; run always,
  //         since vAnimateRingProgressIn only handles .v-ring-progress__arc)
  if (typeof window.vAnimateRingProgressGapIn === 'function') {
    window.vAnimateRingProgressGapIn(container, reducedMotion);
  } else {
    container.querySelectorAll('.v-ring-progress-gap__arc').forEach((arc) => {
      const ring = arc.closest('.v-ring-progress-gap');
      if (!ring) return;

      if (reducedMotion) {
        arc.style.removeProperty('transition');
        arc.style.strokeDashoffset = '0';
        return;
      }

      arc.style.removeProperty('transition');
      arc.classList.remove('is-animating');
      arc.classList.add('is-animating');
      void arc.offsetWidth;
      arc.style.strokeDashoffset = '0';
    });
  }

  // ── 1b. Linear bar fills ──────────────────────────────────────────────────
  // Generic horizontal progress bar: animates `width` from 0 → data-target-width (%).
  // Markup contract:
  //   <div class="...__widget-bar"><i data-bar-fill data-target-width="72" style="width:0%"></i></div>
  // Transition is defined in v-animation.css ([data-bar-fill].is-animating).
  container.querySelectorAll('[data-bar-fill]').forEach((bar) => {
    const target = parseFloat(bar.dataset.targetWidth);
    if (isNaN(target)) return;

    if (reducedMotion) {
      bar.style.transition = 'none';
      bar.style.width = target + '%';
      return;
    }

    bar.classList.remove('is-animating');
    bar.style.width = '0%';
    void bar.offsetWidth;
    bar.classList.add('is-animating');
    bar.style.width = target + '%';
  });

  // ── 2. CountUp numbers (→ snippets/v-count-up.liquid {% javascript %}) ───
  if (!skipCountTargets) {
    if (typeof window.vAnimateCountTarget === 'function') {
      window.vAnimateCountTarget(container, reducedMotion, 1200, { skipRingProgress: true });
    } else {
      vAnimateCountTarget(container, reducedMotion, 1200, { skipRingProgress: true });
    }
  }

  // ── 3. Gradient sparklines (→ snippets/v-widget-hrv-detail.liquid) ───────
  if (typeof window.vAnimateSparklineClipIn === 'function') {
    window.vAnimateSparklineClipIn(container, reducedMotion);
  } else {
    container.querySelectorAll('[data-sparkline-clip]').forEach((svg) => {
      const clip = ensureSparklineClipPath(svg);
      if (!clip) return;

      const { clipRect, totalWidth } = clip;
      const line = svg.querySelector((svg.dataset.sparklineClip || '').split('|')[0]);
      const areaSel = (svg.dataset.sparklineClip || '').split('|')[1];
      const area = areaSel ? svg.querySelector(areaSel) : null;

      if (reducedMotion) {
        if (line) line.removeAttribute('clip-path');
        if (area) { area.removeAttribute('clip-path'); area.style.opacity = '1'; }
        clipRect.setAttribute('width', String(totalWidth));
        return;
      }

      const duration = 1400;
      const startTime = performance.now();
      const ease = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
      const tick = (now) => {
        const t = Math.min((now - startTime) / duration, 1);
        clipRect.setAttribute('width', String(ease(t) * totalWidth));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  // ── 3b. Biology Capital zone column: grow from bottom ──────────────────────
  // Triggered for widgets that contain a biology-capital zone element.
  // Adding is-visible starts the CSS height transition (0 → full height).
  const bioZone = container.querySelector('.v-widget-biology-capital__zone');
  if (bioZone) {
    if (reducedMotion) {
      bioZone.classList.add('is-visible');
    } else {
      setTimeout(() => bioZone.classList.add('is-visible'), 200);
    }
  }

  // ── 4. Solid-stroke lines (stroke-dashoffset draw) ─────────────────────────
  // Detect lines marked with [data-sparkline-dash] on the path/polyline element.
  // Optional paired area: a sibling (or descendant) with [data-sparkline-area].
  container.querySelectorAll('[data-sparkline-dash]').forEach((line) => {
    const areaId = line.dataset.sparklineDash;
    const area   = areaId
      ? (container.querySelector('#' + areaId) || line.parentElement?.querySelector('[data-sparkline-area]'))
      : line.parentElement?.querySelector('[data-sparkline-area]');

    if (reducedMotion) {
      line.style.strokeDashoffset = '0';
      if (area) area.style.opacity = '1';
      return;
    }

    if (!line.style.strokeDasharray) {
      const len = line.getTotalLength();
      line.style.strokeDasharray  = len;
      line.style.strokeDashoffset = len;
    }
    // Transition class defined in v-animation.css
    line.classList.add('is-animating');
    line.style.strokeDashoffset = '0';
    if (area) { area.classList.add('is-animating'); area.style.opacity = '1'; }
  });

  // ── 5. Clock arc widgets (→ snippets/v-widget-stress-eda.liquid) ───────────
  if (!skipRingTargets) {
    if (typeof window.vAnimateStressPeakArcIn === 'function') {
      window.vAnimateStressPeakArcIn(container, reducedMotion);
    } else {
      container.querySelectorAll('.v-widget-stress__peak-arc[data-target-offset], .v-the-app-energy-tabs__stress-clock-arc[data-target-offset]').forEach((arc) => {
        const targetOffset = parseFloat(arc.dataset.targetOffset);
        if (isNaN(targetOffset)) return;

        const svg = arc.closest('svg');
        const dot = svg && svg.querySelector('.v-widget-stress__peak-dot, .v-the-app-energy-tabs__stress-clock-dot');

        if (reducedMotion) {
          arc.style.strokeDashoffset = targetOffset;
          if (dot) dot.classList.add('is-visible');
          return;
        }

        arc.style.strokeDashoffset = targetOffset;
        if (dot) dot.classList.add('is-visible');
      });
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * After a bounceIn animation finishes on `el`, strip the animation classes
 * and inline styles so no `animation: ... forwards` fill persists.
 * A lingering forwards-fill creates a compositing layer whose opacity
 * isolation breaks `backdrop-filter` on descendant elements.
 * Replaces with the static `v-widget--bounce-in-done` class.
 *
 * Three widget flavours:
 *   1. glass-card  — animation runs on __body; __bg uses CSS transition
 *   2. backdrop widgets (sleep/hrv/energy) — animation runs on the child
 *      widget root (.v-widget-sleep etc.) instead of the container
 *   3. generic (v-widget-top) — animation runs on the container itself
 */
function _cleanupBounceIn(el) {
  const body = el.querySelector('.v-glass-card-float__body');
  const backdropChild = el.querySelector('.v-widget-sleep, .v-widget-hrv, .v-widget-energy');

  const animTarget = body || backdropChild || el;

  animTarget.addEventListener('animationend', () => {
    el.classList.remove('v-widget--bounce-in');
    el.classList.add('v-widget--bounce-in-done');
    el.style.animation = '';
    el.style.animationDelay = '';
    if (body) {
      body.style.animation = '';
      body.style.animationDelay = '';
      body.style.opacity = '1';
      body.style.transform = 'none';
      const bg = el.querySelector('.v-glass-card-float__bg');
      if (bg) bg.style.transitionDelay = '';
    }
    if (backdropChild) {
      backdropChild.style.animation = '';
      backdropChild.style.animationDelay = '';
      backdropChild.style.opacity = '1';
      backdropChild.style.transform = 'none';
    }
  }, { once: true });
}

/**
 * VWidgetAnimate
 *
 * Scroll-triggered animation for standalone v-widget-* snippets rendered
 * outside of v-hero-technology (e.g. inside v-multi-column items).
 *
 * Observes every [data-widget-type] element that is NOT already inside a
 * v-hero-technology element (hero panels use section-local VHeroTechnologyAnimate).
 * On viewport entry, delegates all animation to vAnimateWidgetContainer().
 */
class VWidgetAnimate {
  constructor() {
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this._observer = new IntersectionObserver(this._onIntersect.bind(this), {
      threshold: 0.25,
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._setup());
    } else {
      this._setup();
    }
  }

  _setup() {
    document.querySelectorAll('[data-widget-type]').forEach((widget) => {
      if (widget.closest('v-hero-technology')) return;
      if (widget.closest('v-expand-cards')) return;
      if (widget.closest('v-ring-journey')) return;
      this._observer.observe(widget);
    });
  }

  _onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this._observer.unobserve(entry.target);
      vAnimateWidgetContainer(entry.target, this._reducedMotion);
    });
  }
}

new VWidgetAnimate();

window.vAnimateWidgetContainer = vAnimateWidgetContainer;




// ─────────────────────────────────────────────────────────────────────────────

/**
 * VNavCardHeightSync
 *
 * Synchronises the v-nav-card floating-card height transition with the base
 * Motion panel slide-in (0.3s) so users see one fluid motion instead of two
 * staged movements.
 *
 * Why this exists:
 * - The base theme (theme.js NavigationDrawer.switchToPanel) animates panels
 *   with Motion's Web Animations API. Intermediate frames live inside WAAPI
 *   and inline `style.visibility` is only written when the animation ends
 *   (after ~0.6s). A CSS selector that triggers on `[style*="visibility:"]`
 *   therefore fires *after* the panel has fully slid in, causing the card to
 *   jump-grow at the very end — the visible "collapse → expand" double-render
 *   the user reported.
 *
 * Approach:
 * - On bubble-phase click of `button[data-panel]`, toggle a `.v-nav-card--level-2`
 *   class on the drawer host in the same frame Motion starts.
 * - Drive the wrapper height transition entirely via inline-style pixels.
 *   CSS only owns panel positioning under `.v-nav-card--level-2`; trying to
 *   transition `height: 100% ↔ auto` directly is unreliable in Chromium
 *   because the simultaneous panel `position: static ↔ absolute` swap
 *   collapses the resolved height to 0px on the first frame even with
 *   `interpolate-size: allow-keywords`.
 * - Per-call sequence (both directions):
 *     1. Read fromH = wrapper.getBoundingClientRect().height.
 *     2. Pin wrapper.style.height = fromH (with transition: none) and flush.
 *     3. Toggle the level class; wrapper height stays pinned so no jump.
 *     4. Temporarily release the wrapper to height: auto (transition: none),
 *        read naturalH, restore the pin in the same synchronous step.
 *     5. rAF → set wrapper.style.height = toH; CSS transition animates.
 *     6. transitionend → drop transition prop. Inline height STAYS so the
 *        wrapper has a stable hand-off (clearing it forces CSS to re-resolve
 *        against a circular grid sizing chain).
 * - Drawer close (`dialog:after-hide`) is the only place inline height and
 *   level class are wiped, so the next open starts fresh.
 *
 * Only active on mobile/tablet (matches the @media (max-width: 1149px) scope
 * of the v-nav-card styles).
 */
class VNavCardHeightSync {
  constructor() {
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._mqMobile = window.matchMedia('(max-width: 1149px)');

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._setup());
    } else {
      this._setup();
    }
  }

  _setup() {
    const drawers = document.querySelectorAll('.navigation-drawer.v-nav-card');
    drawers.forEach((drawer) => this._bindDrawer(drawer));
  }

  _bindDrawer(drawer) {
    // Bubble-phase listener so it runs AFTER theme.js's switchLinkList() has
    // synchronously toggled `[hidden]` on the target .panel__wrapper. Reading
    // panel heights any earlier would measure a still-hidden wrapper.
    drawer.addEventListener('click', (e) => {
      if (!this._mqMobile.matches) return;
      const btn = e.target.closest('button[data-panel]');
      if (!btn || !drawer.contains(btn)) return;

      const dataPanel = btn.getAttribute('data-panel') || '';
      const parts = dataPanel.split('-');
      const panelIndex = parseInt(parts[0], 10);

      // panel index 0 = back to level-1; >=1 = enter level-2 (mega-menu)
      const targetLevel = panelIndex === 0 ? 1 : 2;
      this._switchLevel(drawer, targetLevel);
    });

    // Reset on drawer close so reopening always starts at level-1.
    drawer.addEventListener('dialog:after-hide', () => {
      this._resetDrawer(drawer);
    });
  }

  _switchLevel(drawer, targetLevel) {
    const wrapper = drawer.querySelector('.panel-list__wrapper');
    if (!wrapper) return;

    const isCurrentlyLevel2 = drawer.classList.contains('v-nav-card--level-2');
    const willBeLevel2 = targetLevel === 2;
    if (isCurrentlyLevel2 === willBeLevel2) return;

    this._cleanupTransition(drawer);

    if (this._reducedMotion) {
      drawer.classList.toggle('v-nav-card--level-2', willBeLevel2);
      if (!willBeLevel2) wrapper.style.height = '';
      return;
    }

    const fromH = wrapper.getBoundingClientRect().height;

    // Pin starting height (suppress transition for the pin).
    wrapper.style.transition = 'none';
    wrapper.style.height = `${fromH}px`;
    void wrapper.offsetHeight; // flush
    wrapper.style.transition = '';

    // Toggle class — CSS swaps panel positioning but wrapper height stays
    // pinned, so the user sees no jump in this frame.
    drawer.classList.toggle('v-nav-card--level-2', willBeLevel2);

    // Measure target height by temporarily releasing the wrapper to grow
    // freely, reading the resulting natural height, then restoring the pin.
    // This avoids circular sizing dependencies (wrapper height ↔ panel
    // height ↔ scroller height) that would otherwise force the wrapper to
    // re-resolve to its current inline-pin value rather than the target.
    const prevHeight = wrapper.style.height;
    const prevTransition = wrapper.style.transition;
    wrapper.style.transition = 'none';
    wrapper.style.height = 'auto';
    // Force layout so offsetHeight reflects the natural (unconstrained) size.
    const naturalH = wrapper.offsetHeight;
    // Restore pin in the SAME synchronous step so the user never sees the
    // unconstrained state paint.
    wrapper.style.height = prevHeight;
    void wrapper.offsetHeight;
    wrapper.style.transition = prevTransition;

    // Cap to drawer body row height so we never overshoot available space.
    const maxH = wrapper.parentElement
      ? wrapper.parentElement.getBoundingClientRect().height
      : naturalH;
    const toH = Math.min(naturalH, maxH);

    // Schedule the target on the next frame so the transition has both
    // explicit endpoints.
    requestAnimationFrame(() => {
      wrapper.style.height = `${toH}px`;
    });

    const onEnd = (e) => {
      if (e.target !== wrapper || e.propertyName !== 'height') return;
      this._finishTransition(drawer);
    };
    wrapper.addEventListener('transitionend', onEnd);
    drawer._vNavCardOnEnd = onEnd;

    // Safety fallback in case transitionend doesn't fire.
    drawer._vNavCardTimer = window.setTimeout(() => {
      this._finishTransition(drawer);
    }, 700);
  }

  _finishTransition(drawer) {
    const wrapper = drawer.querySelector('.panel-list__wrapper');
    if (!wrapper) return;
    if (drawer._vNavCardOnEnd) {
      wrapper.removeEventListener('transitionend', drawer._vNavCardOnEnd);
      drawer._vNavCardOnEnd = null;
    }
    if (drawer._vNavCardTimer) {
      window.clearTimeout(drawer._vNavCardTimer);
      drawer._vNavCardTimer = null;
    }
    wrapper.style.transition = '';
    // Keep inline pixel height in place. Clearing it would force CSS to
    // recompute `height: 100%` against an indirectly-defined parent which
    // resolves inconsistently (the base layout uses a grid row that depends
    // on the panel heights, creating a circular sizing chain). Inline px
    // is the only stable hand-off for both level-1 and level-2.
  }

  // Cancel any pending listener / timer without touching inline height.
  // The inline height is the stable hand-off between sub-transitions; if we
  // cleared it here, the next _switchLevel call would read a corrupted
  // (recomputed-from-CSS) fromH baseline.
  _cleanupTransition(drawer) {
    const wrapper = drawer.querySelector('.panel-list__wrapper');
    if (!wrapper) return;
    if (drawer._vNavCardOnEnd) {
      wrapper.removeEventListener('transitionend', drawer._vNavCardOnEnd);
      drawer._vNavCardOnEnd = null;
    }
    if (drawer._vNavCardTimer) {
      window.clearTimeout(drawer._vNavCardTimer);
      drawer._vNavCardTimer = null;
    }
    wrapper.style.transition = '';
  }

  // Full reset, used when the drawer closes so the next open starts fresh.
  _resetDrawer(drawer) {
    this._cleanupTransition(drawer);
    const wrapper = drawer.querySelector('.panel-list__wrapper');
    if (wrapper) wrapper.style.height = '';
    drawer.classList.remove('v-nav-card--level-2');
  }
}

new VNavCardHeightSync();

/**
 * VNavCardFadeTransition
 *
 * 在移动端/平板（max-width: 1149px）将 `.v-nav-card` 导航 drawer 的展示/关闭
 * 动效由基类 `NavigationDrawer` 默认的 `clip-path` 滑入 / 滑出
 * 替换为纯 opacity 淡入淡出，不区分当前展示的是一级菜单还是二级菜单。
 *
 * 实现方式：
 * - 等待 `navigation-drawer` 自定义元素定义完成后，对其原型上的
 *   `_showTransition` / `_hideTransition` 做条件覆盖。
 * - 命中条件：实例携带 `.v-nav-card` 且当前视口匹配移动端断点。
 *   未命中时回退到基类原始实现，保证桌面端 mega-menu drawer 等不受影响。
 * - 使用原生 Web Animations API（`element.animate`）实现 0.2s 的 opacity
 *   过渡，规避基类 clip-path 内联样式导致 CSS 难以覆盖的问题。
 * - 同时清理基类可能残留的 inline `clip-path`，避免下次展开仍以裁剪态出现。
 * - 遵循 `prefers-reduced-motion`：用 0ms 过渡瞬时切换可见性。
 *
 * 注意：基类内部读取 `animationControls.finished.then(...)`，因此
 * 覆盖后的方法必须返回一个 Promise 兼容对象。
 */
class VNavCardFadeTransition {
  constructor() {
    this._mqMobile = window.matchMedia('(max-width: 1149px)');
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!('customElements' in window)) return;

    window.customElements.whenDefined('navigation-drawer').then(() => {
      const NavigationDrawerCtor = window.customElements.get('navigation-drawer');
      if (!NavigationDrawerCtor) return;
      this._patchPrototype(NavigationDrawerCtor.prototype);
    });
  }

  _shouldUseFade(drawer) {
    return this._mqMobile.matches && drawer.classList.contains('v-nav-card');
  }

  _patchPrototype(proto) {
    const origShow = proto._showTransition;
    const origHide = proto._hideTransition;
    const self = this;

    proto._showTransition = function (animate = true) {
      if (!self._shouldUseFade(this)) {
        return origShow.call(this, animate);
      }
      return self._runShow(this, animate);
    };

    proto._hideTransition = function () {
      if (!self._shouldUseFade(this)) {
        return origHide.call(this);
      }
      return self._runHide(this);
    };
  }

  // Clear residual inline clip-path on the shadow `[part="content"]` so the
  // base-class clip-path animation (left over from a previous open) does not
  // re-clip the drawer when we are doing a pure-opacity fade.
  _resetClipPath(drawer) {
    const content = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="content"]');
    if (content) {
      content.style.clipPath = 'none';
      const children = content.children;
      for (let i = 0; i < children.length; i += 1) {
        children[i].style.opacity = '';
      }
    }
    const closeButton = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="outside-close-button"]');
    if (closeButton) closeButton.style.opacity = '';
  }

  _setInitialPosition(drawer) {
    // Mirror the base Drawer._setInitialPosition for `bottom`/`left` mobile
    // opening so the drawer lays out correctly even though we skip the
    // clip-path animation.
    const openFrom = drawer.getAttribute('mobile-opening') || drawer.getAttribute('open-from') || 'bottom';
    drawer.style.left = (document.dir === 'ltr' && openFrom === 'left') ? '0px' : 'auto';
    drawer.style.right = drawer.style.left === 'auto' ? '0px' : 'auto';
    drawer.style.bottom = openFrom === 'bottom' ? '0px' : null;
    drawer.style.top = drawer.style.bottom === '' ? '0px' : null;
  }

  _runShow(drawer, animate) {
    this._setInitialPosition(drawer);
    this._resetClipPath(drawer);

    const duration = this._reducedMotion || !animate ? 0 : 220;
    const anim = drawer.animate(
      [
        { opacity: 0, visibility: 'visible' },
        { opacity: 1, visibility: 'visible' },
      ],
      { duration, easing: 'ease-out', fill: 'forwards' }
    );

    return anim.finished
      .catch(() => {})
      .then(() => {
        drawer.style.opacity = '';
        drawer.style.visibility = '';
        drawer.classList.add('show-close-cursor');
      });
  }

  _runHide(drawer) {
    this._resetClipPath(drawer);

    const duration = this._reducedMotion ? 0 : 220;
    const anim = drawer.animate(
      [
        { opacity: 1, visibility: 'visible' },
        { opacity: 0, visibility: 'hidden' },
      ],
      { duration, easing: 'ease-in', fill: 'forwards' }
    );

    return anim.finished
      .catch(() => {})
      .then(() => {
        drawer.classList.remove('show-close-cursor');
      });
  }
}

new VNavCardFadeTransition();

// ─── GSAP dynamic loader ───────────────────────────────────────────────────
// GSAP is the default engine for new complex animations (per the project's
// animation-conventions rule). It is loaded on demand via this single shared
// promise so pages with no GSAP-driven section never pull in the bundle.
// `gsap` and `gsap/ScrollTrigger` resolve through the import map in
// layout/theme.liquid (flat assets/gsap.min.js + assets/gsap-scrolltrigger.min.js).
let _gsapPromise = null;

/**
 * Load GSAP core and the ScrollTrigger plugin once, shared across all
 * GSAP-driven animation classes.
 *
 * @returns Promise resolving to `{ gsap, ScrollTrigger }`, with the plugin
 *   already registered. The promise is cached so repeated calls are cheap.
 * @remarks Resolves the same instance to every caller so ScrollTrigger is
 *   wired to a single gsap core. Rejects only if the bundle fails to load.
 */
function loadGsap() {
  if (_gsapPromise) return _gsapPromise;
  _gsapPromise = Promise.all([
    import('gsap'),
    import('gsap/ScrollTrigger'),
  ]).then(([g, s]) => {
    const gsap = g.gsap || g.default;
    const ScrollTrigger = s.ScrollTrigger || s.default;
    gsap.registerPlugin(ScrollTrigger);
    return { gsap, ScrollTrigger };
  });
  return _gsapPromise;
}

window.loadGsap = loadGsap;

export { vAnimateWidgetContainer, _cleanupBounceIn };
